// Notification preferences and support contract adapter.
//
// geo-api is authoritative.
// Preference persistence is row-based:
//   GET /notifications/me/preferences
//   PUT /notifications/me/preferences
//
// Support ticket creation:
//   POST /support
//
// No frontend business data is persisted locally.

import { geoApi, type GeoApiResult } from '@/lib/geoApi';

export interface NotificationPreferences {
  email: boolean;
  sms: boolean;
  whatsapp: boolean;
  whatsappNumber?: string;
  renewalReminders: boolean;
  paymentReminders: boolean;
}

interface NotificationPreferenceRow {
  id?: string;
  user_id?: string;
  channel: string;
  event_code?: string | null;
  enabled: boolean;
  consent_source?: string | null;
  consent_given_at?: string | null;
  consent_revoked_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface PreferenceEnvelope {
  data?: NotificationPreferenceRow[];
}

export interface SupportEnquiryPayload {
  subject: string;
  category: 'general' | 'billing' | 'data' | 'technical' | string;
  message: string;
  whatsappOptIn?: boolean;
  whatsappNumber?: string;
}

interface SupportTicketRecord {
  id: string;
  ticket_no?: string;
  subject?: string;
  status?: string;
}

const FALLBACK_PREFS: NotificationPreferences = {
  email: true,
  sms: false,
  whatsapp: false,
  renewalReminders: true,
  paymentReminders: true,
};

function preferenceValue(
  rows: NotificationPreferenceRow[],
  channel: string,
  eventCode: string | null,
  fallback: boolean
): boolean {
  const exact = rows.find(
    (row) =>
      row.channel === channel &&
      (row.event_code ?? null) === eventCode
  );

  if (exact) return Boolean(exact.enabled);

  if (eventCode !== null) {
    const channelWide = rows.find(
      (row) =>
        row.channel === channel &&
        (row.event_code ?? null) === null
    );

    if (channelWide) return Boolean(channelWide.enabled);
  }

  return fallback;
}

async function putPreference(
  channel: string,
  eventCode: string | null,
  enabled: boolean
): Promise<GeoApiResult<PreferenceEnvelope>> {
  return geoApi.put<PreferenceEnvelope>(
    '/notifications/me/preferences',
    {
      channel,
      eventCode,
      enabled,
      consentSource: 'portal_self_service',
    }
  );
}

export const notificationService = {
  async getPreferences(): Promise<NotificationPreferences> {
    const r = await geoApi.get<PreferenceEnvelope>(
      '/notifications/me/preferences'
    );

    const rows = Array.isArray(r.data?.data) ? r.data.data : [];

    if (r.error) {
      return { ...FALLBACK_PREFS };
    }

    return {
      email: preferenceValue(rows, 'email', null, true),
      sms: preferenceValue(rows, 'sms', null, false),
      whatsapp: preferenceValue(rows, 'whatsapp', null, false),

      renewalReminders: preferenceValue(
        rows,
        'email',
        'renewal_reminder',
        true
      ),

      paymentReminders: preferenceValue(
        rows,
        'email',
        'payment_reminder',
        true
      ),
    };
  },

  async updatePreferences(
    prefs: NotificationPreferences
  ): Promise<GeoApiResult<PreferenceEnvelope>> {
    const operations = [
      ['email', null, prefs.email],
      ['sms', null, prefs.sms],
      ['whatsapp', null, prefs.whatsapp],
      ['email', 'renewal_reminder', prefs.renewalReminders],
      ['email', 'payment_reminder', prefs.paymentReminders],
    ] as const;

    let lastResult: GeoApiResult<PreferenceEnvelope> = {
      data: null,
      error: null,
      status: 200,
    };

    for (const [channel, eventCode, enabled] of operations) {
      const result = await putPreference(channel, eventCode, enabled);

      if (result.error) {
        return result;
      }

      lastResult = result;
    }

    return lastResult;
  },

  submitEnquiry(payload: SupportEnquiryPayload) {
    return geoApi.post<{ data?: SupportTicketRecord }>(
      '/support',
      {
        subject: payload.subject,
        description: payload.message,
        category: payload.category,
        priority: 'normal',
        metadata: {
          source: 'investor_portal',
          whatsappOptIn: Boolean(payload.whatsappOptIn),
          ...(payload.whatsappNumber
            ? { whatsappNumber: payload.whatsappNumber }
            : {}),
        },
      }
    );
  },
};
